import React, { useState, useMemo } from 'react';
import mainTheme from '../ProjectSettings.module.scss';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
    setProjectTerms,
    getProjectTerms,
    getProject,
    setProjectOptionTerms,
    getProjectOptionTerms,
} from '@/services/projects';
import { useAsyncState } from '@/utils/hooks';
import TermsModal from '@/components/TermsModal/TermsModal';
import InfoIconWithTooltip from '@/seller/components/InfoIconWithTooltip';
import theme from './ProjectSettingsTerms.module.scss';
import { Terms } from '@/services/terms';
import { Input, MarkdownEditor, TextArea, classnames, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { OptionTermsDto, ProjectTerms } from '@/types';
import { Button, ButtonBar, SlideToggle } from '@ygtechnologies/rubus-ui';

interface ProjectSettingsPrivacyPolicyProps {
    projectId: number;
}

const ProjectSettingsTerms: React.FC<ProjectSettingsPrivacyPolicyProps> = ({ projectId }) => {
    const [showPreview, setShowPreview] = useState(false);
    const [project] = useAsyncState(() => getProject(projectId), [projectId], null);

    const [terms, setTerms] = useAsyncState(() => getProjectTerms(projectId), [], null);
    const [optionTerms, setOptionTerms] = useAsyncState(() => getProjectOptionTerms(projectId), [], null);
    const [isSaving, setIsSaving] = useState(false);

    const onSave = async (e) => {
        if (!terms) return;

        e.preventDefault();
        setIsSaving(true);

        await setProjectTerms(projectId, terms);
        Toast.success(<Trans>Saved Terms and conditions &amp; privacy policy</Trans>);
        setIsSaving(false);
    };

    const onOptionsTermsSave = async (e) => {
        if (!optionTerms) return;

        e.preventDefault();
        setIsSaving(true);

        await setProjectOptionTerms(projectId, {
            optionTermsEnabled: optionTerms.optionTermsEnabled,
            optionTermsText: optionTerms.optionTermsText,
        });
        Toast.success(<Trans>Saved options terms</Trans>);
        setIsSaving(false);
    };
    const handleChange = (changes: Partial<ProjectTerms>) => setTerms((prevValue) => ({ ...prevValue!, ...changes }));

    const handleOptionTermsChange = (changes: Partial<OptionTermsDto>) =>
        setOptionTerms((prevValue) => ({
            optionTermsEnabled: false,
            optionTermsText: '',
            ...(prevValue ?? {}),
            ...changes,
        }));

    const mockTerms = useMemo<Terms>(
        () => ({
            bygrTermsAcceptRequired: true,
            sellerTermsAcceptRequired: true,
            sellerPrivacyPolicyUrl: terms?.externalPrivacyPolicyLink ?? '',
            sellerPrivacyPolicyName: terms?.externalPrivacyPolicyName ?? '',
            sellerTermsText: terms?.termsAndConditions ?? '',
        }),
        [terms]
    );

    return (
        <>
            <form className={classnames(mainTheme.section, 'form')}>
                <label>
                    <h5>
                        <Trans>Terms and conditions</Trans>
                    </h5>
                    <TextArea
                        autoResize
                        minRows={3}
                        className="form-control"
                        placeholder={t`Project terms and conditions`}
                        value={terms?.termsAndConditions ?? ''}
                        onChange={(e) => handleChange({ termsAndConditions: e.target.value })}
                    />
                </label>

                <label>
                    <h5>
                        <Trans>Project privacy policy link</Trans>
                        <InfoIconWithTooltip content={<Trans>Link to your own privacy policy.</Trans>} />
                    </h5>
                    <Input
                        placeholder={t`Privacy policy link`}
                        className="form-control"
                        value={terms?.externalPrivacyPolicyLink ?? ''}
                        onChange={(e) => handleChange({ externalPrivacyPolicyLink: e.target.value })}
                    />
                </label>

                <label>
                    <h5>
                        <Trans>Name of privacy link (connected to company)</Trans>
                    </h5>
                    <Input
                        placeholder={t`Write company privacy policy link...`}
                        className="form-control"
                        value={terms?.externalPrivacyPolicyName ?? ''}
                        onChange={(e) => handleChange({ externalPrivacyPolicyName: e.target.value })}
                    />
                    <p>
                        <Trans>
                            Use this field if the project should use another company’s privacy policy, such as the developer
                            instead of the contractor.
                        </Trans>
                    </p>
                </label>

                <ButtonBar>
                    <ButtonBar.Right>
                        <Button variant="tertiary" onClick={() => setShowPreview(true)}>
                            <Trans>Preview terms window</Trans>
                        </Button>
                        <Button variant="primary" onClick={onSave}>
                            <Trans>Save project terms</Trans>
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>

                <hr />

                <div className="form-label">
                    <h5>
                        <Trans>Options terms and conditions</Trans>
                    </h5>

                    <SlideToggle
                        className={theme.warningTextToggle}
                        onChange={(e) =>
                            handleOptionTermsChange({
                                optionTermsEnabled: e.target.checked,
                            })
                        }
                        checked={optionTerms?.optionTermsEnabled ?? false}>
                        {optionTerms?.optionTermsEnabled ? <Trans>Enabled</Trans> : <Trans>Disabled</Trans>}
                    </SlideToggle>
                    {optionTerms?.optionTermsEnabled && (
                        <MarkdownEditor
                            required
                            className="form-control"
                            fieldName={t`Options terms and conditions`}
                            markdown={optionTerms?.optionTermsText ?? ''}
                            // disabled={!terms.optionsTermsEnabled}
                            previewClassName={theme.markdownPreview}
                            onChange={(value) => handleOptionTermsChange({ optionTermsText: value })}
                        />
                    )}
                </div>
                <ButtonBar>
                    <ButtonBar.Right>
                        <Button onClick={onOptionsTermsSave} variant="primary" loading={isSaving}>
                            <Trans>Save options terms</Trans>
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </form>

            <TermsModal
                checked={true}
                isOpen={showPreview}
                isSaving={false}
                onAcceptClick={() => setShowPreview(false)}
                onCheckChange={() => null}
                profileName={project?.sellerProfileName ?? ''}
                terms={mockTerms}
            />
        </>
    );
};

export default ProjectSettingsTerms;
